import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Eye, Loader2, ShieldCheck } from "lucide-react";
import RequestAccessModal from "../../components/RequestAccessModal";
import { useAuth } from "../../lib/auth";

export function CtaSection() {
  const [showReq, setShowReq] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { demoLogin } = useAuth();
  const navigate = useNavigate();

  const startDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      const u = await demoLogin();
      navigate(u?.is_demo ? "/assistant?demo=1" : "/dashboard");
    } catch (e) {
      console.error("demo-login failed", e);
      setDemoLoading(false);
    }
  };

  return (
    <section className="border-t border-[var(--border)] bg-[var(--surface)]">
      <RequestAccessModal open={showReq} onClose={() => setShowReq(false)} />
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline">// 06_ENGAGE</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tight mt-3">
              See it before you ask for a quote.<br/>
              <span className="text-[var(--cyan)] cyan-text-glow">Walk the live demo.</span>
            </h2>
            <p className="mt-6 text-[var(--text-2)] max-w-2xl text-base">
              DefDossier is sales-led — every account is provisioned manually so we can match the right pricing and onboarding to your team. The read-only demo shows you the whole product before that conversation.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="flat-card p-6 space-y-3" data-testid="cta-card">
              <div className="overline">// NEXT_ACTION</div>
              <button
                onClick={startDemo}
                disabled={demoLoading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="cta-demo"
              >
                {demoLoading ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
                {demoLoading ? "Loading demo…" : "Try the Live Demo"} <ArrowRight size={16} />
              </button>
              <button
                onClick={() => setShowReq(true)}
                className="btn-ghost w-full inline-flex items-center justify-center gap-2"
                data-testid="cta-request-access"
              >
                <ShieldCheck size={16} /> Request Full Access
              </button>
              <a
                href="mailto:sales@defdossier.com?subject=DefDossier%20-%20Briefing%20Request"
                className="btn-ghost w-full inline-flex items-center justify-center gap-2"
                data-testid="cta-briefing"
              >
                <Mail size={16} /> Book a 20-Min Briefing
              </a>
              <Link
                to="/login"
                className="font-mono text-xs text-[var(--text-2)] hover:text-white block text-center pt-2"
                data-testid="cta-login"
              >
                // Existing operator? Sign in →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TacticalFooter() {
  return (
    <footer className="border-t border-[var(--border)] py-8">
      <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between items-center gap-3 text-xs font-mono text-[var(--text-2)] tracking-widest uppercase">
        <div>// DEFDOSSIER &middot; TENDER-READY EVIDENCE FOR DEFENCE CYBER TEAMS</div>
        <div className="flex gap-5">
          <a href="#evidence" className="hover:text-white">Evidence</a>
          <a href="#tracks" className="hover:text-white">Tracks</a>
          <a href="#metrics" className="hover:text-white">Outcomes</a>
          <a href="/trust" className="hover:text-white text-[var(--cyan)]" data-testid="footer-trust-link">Trust</a>
        </div>
        <div>BUILD: {new Date().getFullYear()}.02 &middot; CLASS: UNCLASS</div>
      </div>
    </footer>
  );
}
